// src/utils/tenant.js
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { db } from '../firebaseConfig';
import { doc, getDoc, collection } from 'firebase/firestore';

const PROTECTED_PREFIXES = ['/cashup', '/dashboard', '/super'];

function isProtectedPath() {
  try {
    const p = window.location.pathname || '/';
    return PROTECTED_PREFIXES.some((x) => p.startsWith(x));
  } catch (_) {
    return false;
  }
}

function orgKeyForUid(uid) {
  return uid ? `orgId:${uid}` : 'orgId:anon';
}

/**
 * Resolve orgId:
 *  - If logged in AND on protected route: ONLY use claim orgId (prevents cross-org bleed)
 *  - Otherwise (public pages / not logged in): allow URL ?orgId=... then localStorage (anon)
 */
function resolveOrgId({ uid, claimOrgId }) {
  const protectedRoute = isProtectedPath();

  // STRICT mode for app pages: do not fallback to old localStorage orgId
  if (protectedRoute && uid) {
    if (claimOrgId && String(claimOrgId).trim()) {
      const v = String(claimOrgId).trim();
      try {
        localStorage.setItem(orgKeyForUid(uid), v);
      } catch (_) {}
      return v;
    }
    return null; // force guard/no-access instead of bleeding
  }

  // Lenient mode for marketing/public pages
  try {
    const u = new URL(window.location.href);
    const fromUrl = u.searchParams.get('orgId');
    if (fromUrl && fromUrl.trim()) {
      const v = fromUrl.trim();
      try {
        localStorage.setItem(orgKeyForUid(uid), v);
      } catch (_) {}
      return v;
    }
  } catch (_) {}

  try {
    const fromLS = localStorage.getItem(orgKeyForUid(uid));
    return (fromLS && fromLS.trim()) || null;
  } catch (_) {
    return null;
  }
}

export function useOrgId() {
  const { user, claims } = useAuth();
  const uid = user?.uid || null;

  const [orgId, setOrgId] = useState(
    resolveOrgId({ uid, claimOrgId: claims?.orgId })
  );

  // When user changes, recalc using the NEW uid key (prevents cross-login bleed)
  useEffect(() => {
    setOrgId(resolveOrgId({ uid, claimOrgId: claims?.orgId }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  // When claims refresh, keep state + per-user storage in sync
  useEffect(() => {
    setOrgId(resolveOrgId({ uid, claimOrgId: claims?.orgId }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claims?.orgId]);

  // Handle back/forward changes
  useEffect(() => {
    const onPop = () => setOrgId(resolveOrgId({ uid, claimOrgId: claims?.orgId }));
    window.addEventListener('popstate', onPop);
    window.addEventListener('hashchange', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      window.removeEventListener('hashchange', onPop);
    };
  }, [uid, claims?.orgId]);

  return orgId;
}

/**
 * Get the org name from /orgs/{orgId}.name (fallbacks sensibly if missing).
 */
export async function getOrgName(orgId) {
  if (!orgId) return 'Unknown Store';
  try {
    const ref = doc(db, 'orgs', orgId);
    const snap = await getDoc(ref);
    const name = snap.exists() ? (snap.data().name || '').trim() : '';
    return name || 'Unknown Store';
  } catch (e) {
    console.warn('getOrgName failed', e);
    return 'Unknown Store';
  }
}

export function orgCol(orgId, subcollectionName) {
  return collection(db, subcollectionName);
}

export function orgDoc(orgId, subcollectionName, id) {
  return doc(db, subcollectionName, id);
}

export function useOrgMeta() {
  const orgId = useOrgId();

  const orgRef = useMemo(() => {
    return orgId ? doc(db, 'orgs', orgId) : null;
  }, [orgId]);

  const [orgName, setOrgName] = useState('Unknown Store');

  useEffect(() => {
    let isMounted = true;
    (async () => {
      if (!orgId) {
        isMounted && setOrgName('Unknown Store');
        return;
      }
      try {
        const snap = await getDoc(doc(db, 'orgs', orgId));
        const name = snap.exists() ? (snap.data().name || '').trim() : '';
        isMounted && setOrgName(name || 'Unknown Store');
      } catch (e) {
        isMounted && setOrgName('Unknown Store');
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [orgId]);

  return { orgId, orgName, orgRef };
}